import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Hamster — a golden little ball of a hamster, storybook style: one chubby
// round body with the head melting straight into it, hugely stuffed cream
// cheek pouches, tiny round ears, big friendly eyes and tiny paws held up to
// its mouth. Faces +Z, sits on y = 0, about 0.25 m tall.
//
// Rig layout (flat — each named node moves about its own pivot):
//   - "body"  : the golden ball itself. Breathes (squash/stretch about the
//               ground contact) so the hamster never looks dead.
//   - "head"  : the upper-front head cap — cheeks, ears, eyes, nose, teeth.
//               Bobs and dips about the neck; leads the nibbling.
//   - "paw-l" / "paw-r" : the tiny raised paws. Pivot at each shoulder so
//               they can dart up to the mouth.
// The little flat feet and the tail nub are static (unnamed).

const GOLD = "#e2a84e";        // warm golden coat
const GOLD_DARK = "#c1832f";   // shaded back / ear backs
const CREAM = "#f6efe3";       // belly, cheek pouches, paws, teeth
const PINK = "#e8a0a0";        // inner ears, tail nub
const NOSE = "#d97878";        // the tiny pink nose
const EYE_WHITE = "#f7f3e8";   // eye whites (never pure white)
const PUPIL = "#33261c";       // warm dark pupils

// The pivots the animations move the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0, 0];             // ground contact
const HEAD_PIVOT: [number, number, number] = [0, 0.13, 0.02];       // the neck
const SHOULDER_L: [number, number, number] = [-0.055, 0.10, 0.06];  // paw hinges
const SHOULDER_R: [number, number, number] = [0.055, 0.10, 0.06];

// One big friendly eye: white ball, dark pupil slightly forward, tiny highlight.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.024, 10, 8), color: EYE_WHITE,
      position: [x, 0.175, 0.108], name: "head" },
    { geometry: new THREE.SphereGeometry(0.013, 8, 6), color: PUPIL,
      position: [x, 0.175, 0.127], name: "head" },
    { geometry: new THREE.SphereGeometry(0.005, 6, 5), color: CREAM,
      position: [x + 0.006, 0.182, 0.136], name: "head" },
  ];
}

// One tiny round ear: a flattened golden disc with a pink inner disc.
function ear(side: 1 | -1): Part[] {
  const x = side * 0.055;
  return [
    { geometry: new THREE.SphereGeometry(0.028, 8, 8), color: GOLD_DARK,
      position: [x, 0.235, 0.02], rotation: [0, 0, -side * 0.3],
      scale: [1.0, 1.0, 0.45], name: "head" },
    { geometry: new THREE.SphereGeometry(0.017, 8, 6), color: PINK,
      position: [x, 0.233, 0.031], rotation: [0, 0, -side * 0.3],
      scale: [1.0, 1.0, 0.4], name: "head" },
  ];
}

// One tiny raised paw: a stub of an arm with a cream paw ball, held up in
// front of the mouth — the classic hamster "holding a seed" pose.
function paw(side: 1 | -1): Part[] {
  const name = side < 0 ? "paw-l" : "paw-r";
  const x = side * 0.05;
  return [
    // Golden, so the paws read against the cream cheeks and belly behind them.
    { geometry: new THREE.CylinderGeometry(0.014, 0.017, 0.05, 8), color: GOLD,
      position: [x - side * 0.008, 0.095, 0.095], rotation: [-1.2, 0, side * 0.2], name },
    { geometry: new THREE.SphereGeometry(0.021, 8, 6), color: GOLD,
      position: [x - side * 0.014, 0.105, 0.118], name },
  ];
}

export const hamster = defineModel({
  id: "hamster",
  batchId: "mess",
  name: "Hamster",
  tags: ["mess", "cute", "animated", "creature", "animal", "hamster", "rodent", "pet", "mammal"],
  summary: "A golden ball of a hamster with stuffed cheeks and tiny paws held up.",
  description:
    "A storybook hamster: one chubby golden ball of a body with the head " +
    "melting straight into it, hugely stuffed cream cheek pouches, tiny round " +
    "ears, big friendly eyes, two buck teeth and tiny paws held up to its " +
    "mouth. Breathes at rest (idle) and nibbles busily, paws darting to its " +
    "mouth (nibble).",
  author: "models",
  footprint: 0.18,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── body (named, breathes) ──────────────────────────────────────────
      // The golden ball — practically the whole hamster.
      { geometry: new THREE.SphereGeometry(0.105, 14, 12), color: GOLD,
        position: [0, 0.105, -0.005], scale: [1.05, 1.0, 1.1], name: "body" },
      // Cream belly bib on the lower front.
      { geometry: new THREE.SphereGeometry(0.07, 10, 8), color: CREAM,
        position: [0, 0.075, 0.055], scale: [0.95, 0.85, 0.65], name: "body" },
      // No dark saddle: any marking sphere overlapping the ball pokes through
      // the back and reads as a hole, so the ball stays one clean gold mass
      // (the darker shade lives on the ear backs instead).

      // ── head (named, nibbles) ────────────────────────────────────────────
      // The head cap melting into the top-front of the ball.
      { geometry: new THREE.SphereGeometry(0.085, 12, 10), color: GOLD,
        position: [0, 0.165, 0.04], name: "head" },
      // The hugely stuffed cheek pouches bulging out to each side.
      { geometry: new THREE.SphereGeometry(0.048, 10, 8), color: CREAM,
        position: [-0.062, 0.125, 0.075], scale: [1.0, 0.95, 0.95], name: "head" },
      { geometry: new THREE.SphereGeometry(0.048, 10, 8), color: CREAM,
        position: [0.062, 0.125, 0.075], scale: [1.0, 0.95, 0.95], name: "head" },
      // Cream muzzle between the cheeks.
      { geometry: new THREE.SphereGeometry(0.036, 8, 8), color: CREAM,
        position: [0, 0.14, 0.105], scale: [1.1, 0.85, 0.8], name: "head" },
      // Tiny pink nose.
      { geometry: new THREE.SphereGeometry(0.012, 8, 6), color: NOSE,
        position: [0, 0.152, 0.133], name: "head" },
      // Two little buck teeth tucked right under the nose.
      { geometry: new THREE.BoxGeometry(0.02, 0.018, 0.01), color: CREAM,
        position: [0, 0.133, 0.128], name: "head" },
      ...ear(1), ...ear(-1),
      ...eye(0.04), ...eye(-0.04),

      // ── tiny raised paws (named, dart to the mouth) ──────────────────────
      ...paw(-1), ...paw(1),

      // ── feet + tail (static) ─────────────────────────────────────────────
      { geometry: new THREE.SphereGeometry(0.028, 8, 6), color: CREAM,
        position: [-0.045, 0.015, 0.055], scale: [1.0, 0.55, 1.4] },
      { geometry: new THREE.SphereGeometry(0.028, 8, 6), color: CREAM,
        position: [0.045, 0.015, 0.055], scale: [1.0, 0.55, 1.4] },
      // The tiny pink tail nub.
      { geometry: new THREE.SphereGeometry(0.016, 8, 6), color: PINK,
        position: [0, 0.055, -0.115] },
    ];
  },
  animations: [
    // Idle — a quick little breath (small animals breathe fast): the ball
    // swells, the head bobs, the ears give a tiny twitch via the head tilt.
    {
      id: "idle",
      label: "Idle breathing",
      duration: 2.2,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 1.1, value: [1.03, 1.05, 1.02], ease: "sine" },
            { t: 2.2, value: [1, 1, 1], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.1, value: [0, 0.008, 0], ease: "sine" },
            { t: 2.2, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.03, 0, 0.02], ease: "sine" },
            { t: 1.1, value: [-0.03, 0, -0.02], ease: "sine" },
            { t: 2.2, value: [0.03, 0, 0.02], ease: "sine" },
          ],
        },
        {
          target: "paw-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.1, value: [-0.12, 0, 0.06], ease: "sine" },
            { t: 2.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "paw-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [-0.12, 0, -0.06], ease: "sine" },
            { t: 1.1, value: [0, 0, 0], ease: "sine" },
            { t: 2.2, value: [-0.12, 0, -0.06], ease: "sine" },
          ],
        },
      ],
    },

    // Nibble — the signature move: busy, rapid eating. The head dips toward
    // the paws and jitters in tiny fast nods (chewing) while the paws dart
    // alternately up to the mouth.
    {
      id: "nibble",
      label: "Busy nibbling",
      duration: 1.6,
      loop: true,
      tracks: [
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0.18, 0, 0], ease: "easeInOut" },
            { t: 0.2, value: [0.30, 0, 0], ease: "easeInOut" },
            { t: 0.4, value: [0.18, 0, 0], ease: "easeInOut" },
            { t: 0.6, value: [0.30, 0, 0], ease: "easeInOut" },
            { t: 0.8, value: [0.18, 0, 0], ease: "easeInOut" },
            { t: 1.0, value: [0.30, 0, 0], ease: "easeInOut" },
            { t: 1.2, value: [0.18, 0, 0], ease: "easeInOut" },
            { t: 1.4, value: [0.30, 0, 0], ease: "easeInOut" },
            { t: 1.6, value: [0.18, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "paw-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [-0.5, 0, 0.1], ease: "sine" },
            { t: 0.2, value: [-0.15, 0, 0], ease: "sine" },
            { t: 0.4, value: [-0.5, 0, 0.1], ease: "sine" },
            { t: 0.6, value: [-0.15, 0, 0], ease: "sine" },
            { t: 0.8, value: [-0.5, 0, 0.1], ease: "sine" },
            { t: 1.0, value: [-0.15, 0, 0], ease: "sine" },
            { t: 1.2, value: [-0.5, 0, 0.1], ease: "sine" },
            { t: 1.4, value: [-0.15, 0, 0], ease: "sine" },
            { t: 1.6, value: [-0.5, 0, 0.1], ease: "sine" },
          ],
        },
        {
          target: "paw-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [-0.15, 0, 0], ease: "sine" },
            { t: 0.2, value: [-0.5, 0, -0.1], ease: "sine" },
            { t: 0.4, value: [-0.15, 0, 0], ease: "sine" },
            { t: 0.6, value: [-0.5, 0, -0.1], ease: "sine" },
            { t: 0.8, value: [-0.15, 0, 0], ease: "sine" },
            { t: 1.0, value: [-0.5, 0, -0.1], ease: "sine" },
            { t: 1.2, value: [-0.15, 0, 0], ease: "sine" },
            { t: 1.4, value: [-0.5, 0, -0.1], ease: "sine" },
            { t: 1.6, value: [-0.15, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "body", pivot: BODY_PIVOT,
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 0.8, value: [1.02, 0.985, 1.02], ease: "sine" },
            { t: 1.6, value: [1, 1, 1], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
